import {ChangeDetectionStrategy, Component, computed, inject, OnInit, signal} from '@angular/core';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {NzEmptyModule} from 'ng-zorro-antd/empty';
import {NzMessageService} from 'ng-zorro-antd/message';
import {NZ_MODAL_DATA, NzModalRef} from 'ng-zorro-antd/modal';
import {TranslocoPipe} from '@jsverse/transloco';
import {IspOrgApiService} from '../../organization-management/isp-org.api.service';
import {snakeToCamelDeep} from '../../../shared/lib/case';
import {AccountService} from '../../../services/account.service';
import {StorageService} from '../../../services/storage.service';
import {RegionCode} from '../../../app.env.config';
import {isSameSupabaseAuthEnv} from '../../../supabase.env';
import {RegionService} from '../../../services/region.service';
import {SupabaseAuthService} from '../../../services/supabase-auth.service';
import {TranslocoService} from '@jsverse/transloco';

export interface SelectOrgDialogData {
  /** When true, dialog cannot be dismissed without selecting an org. */
  required: boolean;
}

export interface SelectOrgDialogResult {
  orgId: string;
  orgName: string;
  orgRole?: string;
}

@Component({
  selector: 'app-select-org',
  imports: [
    NzButtonModule,
    NzEmptyModule,
    TranslocoPipe,
  ],
  templateUrl: './select-org.component.html',
  styleUrl: './select-org.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectOrgComponent implements OnInit {
  readonly orgList = signal<any[]>([]);
  readonly loading = signal(true);
  readonly regionSwitching = signal(false);
  readonly currentRegion = computed(() => this.regionService.regionCode());
  readonly regionOptions = signal<readonly string[]>([
    RegionCode.US,
    RegionCode.EU,
    RegionCode.CN,
  ]);
  readonly required = inject<SelectOrgDialogData>(NZ_MODAL_DATA).required;

  private readonly orgApiService = inject(IspOrgApiService);
  private readonly storageService = inject(StorageService);
  private readonly accountService = inject(AccountService);
  private readonly regionService = inject(RegionService);
  private readonly supabaseAuthService = inject(SupabaseAuthService);
  private readonly transloco = inject(TranslocoService);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly modalRef = inject(NzModalRef<SelectOrgComponent, SelectOrgDialogResult | null>);

  async ngOnInit() {
    if (await this.regionService.isDebugMode()) {
      this.regionOptions.set([
        RegionCode.US,
        RegionCode.EU,
        RegionCode.CN,
        RegionCode.DEV,
        RegionCode.LOCAL,
        RegionCode.LOCAL_PROD,
      ]);
    }
    await this.loadOrgList();
  }

  async loadOrgList() {
    this.loading.set(true);
    try {
      const res = await this.orgApiService.getUserOrgList();
      if (res.code) {
        this.nzMessageService.error(res.message);
        this.orgList.set([]);
        return;
      }
      this.orgList.set(snakeToCamelDeep(res.result?.items ?? []));
    } finally {
      this.loading.set(false);
    }
  }

  async selectOrg(info: any) {
    const orgId = String(info?.id ?? '');
    const orgName = String(info?.name ?? '');
    if (!orgId) {
      return;
    }

    await this.storageService.lfSet('orgId', orgId);
    await this.storageService.lfSet('orgName', orgName);
    if (info?.roleName) {
      await this.storageService.lfSet('orgRole', info.roleName);
    } else {
      await this.storageService.lfRemove('orgRole');
    }

    this.modalRef.close({
      orgId,
      orgName,
      orgRole: info?.roleName ? String(info.roleName) : undefined,
    });
  }

  async switchRegion(regionCode: string): Promise<void> {
    if (regionCode === this.currentRegion() || this.regionSwitching()) {
      return;
    }

    this.regionSwitching.set(true);
    try {
      const fromRegion = this.currentRegion();
      await this.regionService.setRegion(regionCode);
      if (!isSameSupabaseAuthEnv(fromRegion, regionCode)) {
        this.supabaseAuthService.resetClient();
        this.modalRef.close(null);
        await this.accountService.logout();
        return;
      }
      await this.storageService.lfRemove('orgId');
      await this.storageService.lfRemove('orgName');
      await this.storageService.lfRemove('orgRole');
      await this.loadOrgList();
    } catch {
      this.nzMessageService.error(
        this.transloco.translate('account.select_org.region_switch_failed'),
      );
    } finally {
      this.regionSwitching.set(false);
    }
  }

  async logout() {
    await this.accountService.logout();
    this.modalRef.close(null);
  }
}
